<div class="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/25 to-teal-50/30 text-slate-800 font-sans antialiased pb-20">

  <!-- Sticky Top Header (Glassmorphism, ultra-compact on mobile) -->
  <header class="glass-header sticky top-0 z-40 border-b border-emerald-100 shadow-xs w-full">
    <div class="questionnaire-header-inner mx-auto py-2.5 sm:py-3.5 flex items-center justify-between gap-2 sm:gap-4">
      <!-- Brand / Home Link -->
      <a routerLink="/" class="flex items-center gap-2.5 group">
        <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-md shadow-emerald-500/20 group-hover:scale-105 transition-transform">
          🌿
        </div>
        <div>
          <h1 class="text-xs sm:text-sm md:text-base font-bold text-slate-900 leading-tight">Joy Wellness Check-in</h1>
          <p class="text-[10px] sm:text-xs text-emerald-700 font-medium hidden sm:block">Campus Mental Health Support & Research</p>
        </div>
      </a>

      <!-- Header Status & Progress -->
      <div class="flex items-center gap-2 sm:gap-3">
        <!-- Live status chips (hidden on very small screens, visible from tablet up) -->
        <div *ngIf="progressPercentage > 0" class="hidden md:flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/90 text-white text-[11px] font-medium backdrop-blur-xs">
          <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          <span>{{ friendlyGadLevel }}</span>
          <span class="text-slate-500">•</span>
          <span>{{ friendlyPhqLevel }}</span>
        </div>

        <!-- Progress Badge -->
        <div class="flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full shadow-2xs">
          <span class="text-slate-500 font-semibold hidden xs:inline">Progress:</span>
          <span class="text-emerald-700 font-extrabold">{{ progressPercentage }}%</span>
        </div>
      </div>
    </div>

    <!-- Gradient Progress Bar -->
    <div class="w-full bg-slate-100 h-1">
      <div class="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 h-1 transition-all duration-300"
           [style.width.%]="progressPercentage"></div>
    </div>
  </header>

  <!-- Main Container -->
  <main class="questionnaire-main mx-auto py-5 sm:py-8">

    <!-- Warm Welcome Card -->
    <div *ngIf="!isSubmitted" class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs border border-emerald-100/80 mb-5 sm:mb-8">
      <div class="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
        <span class="px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
          🔒 Safe & Confidential
        </span>
        <span class="px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full bg-blue-50 text-blue-700 text-[10px] sm:text-xs font-semibold">
          No Right or Wrong Answers
        </span>
        <span class="px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full bg-teal-50 text-teal-700 text-[10px] sm:text-xs font-semibold">
          ~4 Minutes
        </span>
      </div>
      
      <h2 class="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2.5">
        How have you really been feeling lately?
      </h2>
      <p class="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed mb-4">
        Being a university student comes with intense pressure — academic deadlines, exam anxiety, endless lectures, campus living, side jobs, and burnout. 
        We are building <strong>Joy AI</strong> to be a compassionate, non-judgmental companion that knows how to truly listen and respond when campus life gets heavy. 
        Your authentic responses will directly teach Joy how to comfort university students going through stress just like you.
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-3.5 border-t border-slate-100 text-xs text-slate-600">
        <div class="flex items-center gap-2">
          <span class="text-emerald-600 text-base">🔒</span>
          <span>100% Anonymous — no names or emails</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-emerald-600 text-base">💚</span>
          <span>Zero judgment — speak your truth</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-emerald-600 text-base">✨</span>
          <span>Directly improves Joy AI for all students</span>
        </div>
      </div>
    </div>

    <!-- Live Wellness Feeling Summary Card (In normal flow, NO sticky overlap!) -->
    <div *ngIf="!isSubmitted" class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-md border border-slate-700/60 mb-5 sm:mb-8">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3.5 pb-3 border-b border-slate-700/60">
        <div class="flex items-center gap-2.5">
          <div class="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-sm">
            🌱
          </div>
          <div>
            <p class="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider font-semibold">Live Wellness Feeling Monitor</p>
            <p class="text-xs text-slate-300">Calibrated against clinical PHQ-9 & GAD-7 screening benchmarks</p>
          </div>
        </div>
        <span class="text-[11px] text-emerald-400 bg-emerald-950/60 border border-emerald-800/60 px-2.5 py-1 rounded-lg self-start sm:self-auto font-mono">
          Live Real-Time Sync
        </span>
      </div>

      <!-- Two Responsive Status Tiles -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4">
        <div class="bg-slate-800/80 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-700/60 flex items-center justify-between">
          <div>
            <p class="text-[11px] text-slate-400 font-medium">Anxiety & Tension State</p>
            <p class="text-sm sm:text-base font-bold text-amber-400 mt-0.5">{{ friendlyGadLevel }}</p>
          </div>
          <span class="text-xl sm:text-2xl">⚡</span>
        </div>
        <div class="bg-slate-800/80 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-700/60 flex items-center justify-between">
          <div>
            <p class="text-[11px] text-slate-400 font-medium">Mood & Vitality State</p>
            <p class="text-sm sm:text-base font-bold text-teal-400 mt-0.5">{{ friendlyPhqLevel }}</p>
          </div>
          <span class="text-xl sm:text-2xl">🌊</span>
        </div>
      </div>
    </div>

    <!-- The Questionnaire Form -->
    <form *ngIf="!isSubmitted" (ngSubmit)="submitQuestionnaire()" class="space-y-5 sm:space-y-8">

      <!-- SECTION 1: Academic Profile & Routine (Q1 - Q6) -->
      <section class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs border border-emerald-100/80">
        <div class="flex items-center gap-3 mb-5 pb-3.5 border-b border-slate-100">
          <span class="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center text-xs sm:text-sm shadow-xs">1</span>
          <div>
            <h3 class="text-base sm:text-lg font-bold text-slate-900">University Routine & Academic Profile</h3>
            <p class="text-xs text-slate-500">Helps JoyBot understand your daily demands and campus reality.</p>
          </div>
        </div>

        <div class="space-y-5 sm:space-y-6">
          <!-- Q1: Routine -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-2">
              1. What best describes your university routine right now? <span class="text-rose-500">*</span>
            </label>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
              <label *ngFor="let opt of [
                { val: 'University Student (Full-time)', label: 'University student (studying full-time)' },
                { val: 'University Student (Working Part-time)', label: 'Student who also works a part-time job' },
                { val: 'Working Full-time Student', label: 'Working full-time while going to school' }
              ]" class="option-card flex items-center gap-3 p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border cursor-pointer"
                 [ngClass]="formData.status === opt.val ? 'border-emerald-500 bg-emerald-50/70 font-semibold text-emerald-950 ring-1 ring-emerald-500/30 shadow-xs' : 'border-slate-200 bg-slate-50/30 hover:border-emerald-300 text-slate-700'">
                <input type="radio" [(ngModel)]="formData.status" name="status" [value]="opt.val" (change)="calculateProgress()" class="text-emerald-600 accent-emerald-600">
                <span class="text-xs sm:text-sm font-medium leading-snug">{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <!-- Q2: Age Group -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-2">
              2. How old are you? <span class="text-rose-500">*</span>
            </label>
            <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 sm:gap-2.5 text-center">
              <label *ngFor="let age of ['18 - 21', '22 - 25', '26 - 30', '31 - 35', '36 - 40', '41 - 50', '51+']"
                     class="option-card p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border cursor-pointer text-xs sm:text-sm"
                     [ngClass]="formData.age_group === age ? 'border-emerald-500 bg-emerald-50/70 font-bold text-emerald-950 ring-1 ring-emerald-500/30 shadow-xs' : 'border-slate-200 bg-slate-50/30 hover:border-emerald-300 text-slate-700'">
                <input type="radio" [(ngModel)]="formData.age_group" name="age_group" [value]="age" (change)="calculateProgress()" class="text-emerald-600 accent-emerald-600 mr-1.5">
                <span>{{ age === '51+' ? '51+ yrs' : age + ' yrs' }}</span>
              </label>
            </div>
          </div>

          <!-- Q3: Course or Major -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-1.5">
              3. What course or major are you studying? <span class="text-rose-500">*</span>
            </label>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 text-sm">🎓</span>
              <input type="text" [(ngModel)]="formData.field_or_industry" name="field_or_industry" (input)="calculateProgress()" required
                     placeholder="e.g., Computer Science, Medicine, Law, Accounting, Engineering, Mass Communication, Nursing" 
                     class="custom-input w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl border border-slate-200 text-xs sm:text-sm outline-none">
            </div>
          </div>

          <!-- Q4: University Name -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-1.5">
              4. Which university or tertiary institution do you attend? <span class="text-rose-500">*</span>
            </label>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 text-sm">🏛️</span>
              <input type="text" [(ngModel)]="formData.institution_name" name="institution_name" (input)="calculateProgress()" list="nigerianUniversities" required
                     placeholder="e.g., University of Lagos (UNILAG), UI, OAU, UNN, ABU Zaria, Covenant, LASU, FUTA..." 
                     class="custom-input w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl border border-slate-200 text-xs sm:text-sm outline-none">
            </div>
            <datalist id="nigerianUniversities">
              <option value="University of Lagos (UNILAG)"></option>
              <option value="University of Ibadan (UI)"></option>
              <option value="Obafemi Awolowo University (OAU)"></option>
              <option value="University of Nigeria, Nsukka (UNN)"></option>
              <option value="Ahmadu Bello University (ABU Zaria)"></option>
              <option value="University of Benin (UNIBEN)"></option>
              <option value="University of Ilorin (UNILORIN)"></option>
              <option value="Covenant University"></option>
              <option value="Lagos State University (LASU)"></option>
              <option value="Federal University of Technology, Akure (FUTA)"></option>
              <option value="Federal University of Technology, Minna (FUTMINNA)"></option>
              <option value="Babcock University"></option>
              <option value="Bowen University"></option>
              <option value="Nnamdi Azikiwe University (UNIZIK)"></option>
              <option value="University of Port Harcourt (UNIPORT)"></option>
              <option value="Bayero University Kano (BUK)"></option>
              <option value="Rivers State University"></option>
              <option value="Delta State University (DELSU)"></option>
              <option value="Olabisi Onabanjo University (OOU)"></option>
              <option value="Afe Babalola University (ABUAD)"></option>
              <option value="Mountain Top University (MTU)"></option>
            </datalist>
            <p class="text-[11px] text-slate-400 mt-1">Type your school name or acronym, or select from the list.</p>
          </div>

          <!-- Q5: Nigerian Geopolitical Region -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-1.5">
              5. Which geopolitical region in Nigeria is your school located in? <span class="text-rose-500">*</span>
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
              <label *ngFor="let reg of [
                { val: 'South West', zone: 'South West', states: 'Lagos, Ogun, Oyo, Osun, Ondo, Ekiti' },
                { val: 'South South', zone: 'South South', states: 'Rivers, Delta, Edo, Akwa Ibom, Cross River, Bayelsa' },
                { val: 'South East', zone: 'South East', states: 'Anambra, Enugu, Imo, Abia, Ebonyi' },
                { val: 'North Central', zone: 'North Central', states: 'Abuja FCT, Plateau, Kwara, Niger, Benue, Kogi, Nasarawa' },
                { val: 'North West', zone: 'North West', states: 'Kaduna, Kano, Katsina, Sokoto, Kebbi, Zamfara, Jigawa' },
                { val: 'North East', zone: 'North East', states: 'Borno, Adamawa, Bauchi, Gombe, Taraba, Yobe' },
                { val: 'Online / Distance Learning', zone: 'Online / Distance', states: 'Remote / Virtual Learning' }
              ]" class="option-card flex items-start gap-2.5 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border cursor-pointer"
                 [ngClass]="formData.campus_region === reg.val ? 'border-emerald-500 bg-emerald-50/70 font-semibold text-emerald-950 ring-1 ring-emerald-500/30 shadow-xs' : 'border-slate-200 bg-slate-50/30 hover:border-emerald-300 text-slate-700'">
                <input type="radio" [(ngModel)]="formData.campus_region" name="campus_region" [value]="reg.val" (change)="calculateProgress()" class="text-emerald-600 accent-emerald-600 mt-0.5">
                <div class="leading-tight">
                  <span class="font-bold text-xs sm:text-sm text-slate-900 block">{{ reg.zone }}</span>
                  <span class="text-[11px] text-slate-500 font-normal mt-0.5 block">{{ reg.states }}</span>
                </div>
              </label>
            </div>
          </div>

          <!-- Q6: Living Situation -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-2">
              6. Who do you live with while attending school? <span class="text-rose-500">*</span>
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 text-xs sm:text-sm">
              <label *ngFor="let liv of [
                { val: 'Campus dorm or student hostel', label: 'Campus dorm or student hostel', icon: '🏢' },
                { val: 'Off-campus rented apartment / room', label: 'Off-campus rented apartment or room', icon: '🏠' },
                { val: 'Living with parents / family', label: 'Living with parents or family', icon: '👨‍👩‍👦' },
                { val: 'Living with student roommates / friends', label: 'Living with student roommates or friends', icon: '👥' }
              ]" class="option-card flex items-center gap-3 p-3 rounded-xl sm:rounded-2xl border cursor-pointer"
                 [ngClass]="formData.living_situation === liv.val ? 'border-emerald-500 bg-emerald-50/70 font-semibold text-emerald-950 ring-1 ring-emerald-500/30 shadow-xs' : 'border-slate-200 bg-slate-50/30 hover:border-emerald-300 text-slate-700'">
                <input type="radio" [(ngModel)]="formData.living_situation" name="living_situation" [value]="liv.val" (change)="calculateProgress()" class="text-emerald-600 accent-emerald-600">
                <span class="text-base sm:text-lg">{{ liv.icon }}</span>
                <span class="leading-snug">{{ liv.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      <!-- SECTION 2: Anxiety & Nerves Assessment (GAD-7, Q5 - Q11) -->
      <section class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs border border-emerald-100/80">
        <div class="flex items-center justify-between mb-4 pb-3.5 border-b border-slate-100">
          <div class="flex items-center gap-3">
            <span class="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white font-bold flex items-center justify-center text-xs sm:text-sm shadow-xs">2</span>
            <div>
              <h3 class="text-base sm:text-lg font-bold text-slate-900">How have your nerves and anxiety been feeling?</h3>
              <p class="text-xs text-slate-500">Over the last 2 weeks, how often have you felt bothered by any of these?</p>
            </div>
          </div>
        </div>

        <!-- Rating Scale Helper Banner (Fully Responsive) -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5 p-2.5 sm:p-3 bg-slate-50/80 rounded-xl sm:rounded-2xl text-xs text-slate-600 border border-slate-200/60">
          <div class="flex items-center gap-1.5"><span class="w-5 h-5 rounded-full bg-slate-200 font-bold text-slate-800 flex items-center justify-center text-[10px]">0</span> Not at all</div>
          <div class="flex items-center gap-1.5"><span class="w-5 h-5 rounded-full bg-slate-200 font-bold text-slate-800 flex items-center justify-center text-[10px]">1</span> Several days</div>
          <div class="flex items-center gap-1.5"><span class="w-5 h-5 rounded-full bg-slate-200 font-bold text-slate-800 flex items-center justify-center text-[10px]">2</span> More than half the days</div>
          <div class="flex items-center gap-1.5"><span class="w-5 h-5 rounded-full bg-slate-200 font-bold text-slate-800 flex items-center justify-center text-[10px]">3</span> Almost daily</div>
        </div>

        <!-- GAD-7 Items (2x2 grid on mobile, 4 columns on tablet/desktop) -->
        <div class="space-y-4">
          <div *ngFor="let item of [
            { id: 1, key: 'gad_1', text: '5. Feeling nervous, anxious, jittery, or constantly on edge' },
            { id: 2, key: 'gad_2', text: '6. Not being able to stop worrying, or feeling like your mind won\'t shut up' },
            { id: 3, key: 'gad_3', text: '7. Worrying too much about lots of different things all at once' },
            { id: 4, key: 'gad_4', text: '8. Finding it really hard to just sit down, unwind, and truly relax' },
            { id: 5, key: 'gad_5', text: '9. Feeling so restless or jumpy that you can\'t stay still or keep pacing' },
            { id: 6, key: 'gad_6', text: '10. Getting easily annoyed, frustrated, or snapping at small things' },
            { id: 7, key: 'gad_7', text: '11. Feeling a sudden wave of fear or dread, like something awful is about to happen' }
          ]" class="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100 bg-slate-50/40 hover:bg-white transition">
            <p class="font-semibold text-slate-900 text-xs sm:text-sm mb-2.5 leading-relaxed">{{ item.text }}</p>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <label *ngFor="let opt of [
                { val: 0, label: 'Not at all' },
                { val: 1, label: 'Several days' },
                { val: 2, label: 'More than half the days' },
                { val: 3, label: 'Almost daily' }
              ]" class="option-card flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer min-h-[44px]"
                 [ngClass]="formData[item.key] === opt.val ? 'border-emerald-500 bg-emerald-50 font-bold text-emerald-950 ring-1 ring-emerald-500/30 shadow-xs' : 'border-slate-200 bg-white hover:border-emerald-300 text-slate-700'">
                <span class="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-extrabold"
                      [ngClass]="formData[item.key] === opt.val ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'">
                  {{ opt.val }}
                </span>
                <span class="leading-tight text-[11px] sm:text-xs">{{ opt.label }}</span>
                <input type="radio" [(ngModel)]="formData[item.key]" [name]="item.key" [value]="opt.val" (change)="onScoreChange()" class="sr-only">
              </label>
            </div>
          </div>
        </div>
      </section>

      <!-- SECTION 3: Mood, Energy & Rest (PHQ-9, Q12 - Q20) -->
      <section class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs border border-emerald-100/80">
        <div class="flex items-center justify-between mb-4 pb-3.5 border-b border-slate-100">
          <div class="flex items-center gap-3">
            <span class="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white font-bold flex items-center justify-center text-xs sm:text-sm shadow-xs">3</span>
            <div>
              <h3 class="text-base sm:text-lg font-bold text-slate-900">How has your mood, energy, and sleep been?</h3>
              <p class="text-xs text-slate-500">Over the last 2 weeks, how often have you noticed these feelings?</p>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <div *ngFor="let item of [
            { id: 1, key: 'phq_1', text: '12. Having little to no interest or joy in things you usually like doing' },
            { id: 2, key: 'phq_2', text: '13. Feeling down, sad, empty, or hopeless about things' },
            { id: 3, key: 'phq_3', text: '14. Trouble falling or staying asleep, or sleeping too much and still waking up exhausted' },
            { id: 4, key: 'phq_4', text: '15. Feeling completely wiped out, drained, or having almost zero physical energy' },
            { id: 5, key: 'phq_5', text: '16. Losing your appetite (forgetting to eat), or overeating heavily when stressed' },
            { id: 6, key: 'phq_6', text: '17. Feeling bad about yourself — like you are a failure or letting your family down' },
            { id: 7, key: 'phq_7', text: '18. Having a hard time focusing on tasks, like studying for tests or reading slides' },
            { id: 8, key: 'phq_8', text: '19. Moving or talking noticeably slower than usual — or feeling so restless you can\'t sit still' }
          ]" class="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100 bg-slate-50/40 hover:bg-white transition">
            <p class="font-semibold text-slate-900 text-xs sm:text-sm mb-2.5 leading-relaxed">{{ item.text }}</p>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <label *ngFor="let opt of [
                { val: 0, label: 'Not at all' },
                { val: 1, label: 'Several days' },
                { val: 2, label: 'More than half the days' },
                { val: 3, label: 'Almost daily' }
              ]" class="option-card flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer min-h-[44px]"
                 [ngClass]="formData[item.key] === opt.val ? 'border-teal-500 bg-teal-50 font-bold text-teal-950 ring-1 ring-teal-500/30 shadow-xs' : 'border-slate-200 bg-white hover:border-teal-300 text-slate-700'">
                <span class="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-extrabold"
                      [ngClass]="formData[item.key] === opt.val ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'">
                  {{ opt.val }}
                </span>
                <span class="leading-tight text-[11px] sm:text-xs">{{ opt.label }}</span>
                <input type="radio" [(ngModel)]="formData[item.key]" [name]="item.key" [value]="opt.val" (change)="onScoreChange()" class="sr-only">
              </label>
            </div>
          </div>

          <!-- Q20: Safe Space Screen -->
          <div class="p-3.5 sm:p-5 rounded-xl sm:rounded-2xl border border-rose-200 bg-rose-50/50 hover:bg-rose-50/80 transition">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="text-rose-700 font-bold text-[11px] uppercase tracking-wider bg-rose-100 px-2 py-0.5 rounded-md">SAFE SPACE SCREEN</span>
              <span class="text-xs text-rose-600 font-medium">(Your wellbeing matters)</span>
            </div>
            <p class="font-semibold text-slate-900 text-xs sm:text-sm mb-3 leading-relaxed">
              20. Heavy thoughts that you'd be better off not waking up, or thoughts of hurting yourself
            </p>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
              <label *ngFor="let opt of [
                { val: 0, label: 'Not at all' },
                { val: 1, label: 'Several days' },
                { val: 2, label: 'More than half the days' },
                { val: 3, label: 'Almost daily' }
              ]" class="option-card flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer min-h-[44px]"
                 [ngClass]="formData.phq_9 === opt.val ? 'border-rose-500 bg-rose-100 font-bold text-rose-950 ring-1 ring-rose-500/30 shadow-xs' : 'border-slate-200 bg-white hover:border-rose-300 text-slate-700'">
                <span class="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-extrabold"
                      [ngClass]="formData.phq_9 === opt.val ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-600'">
                  {{ opt.val }}
                </span>
                <span class="leading-tight text-[11px] sm:text-xs">{{ opt.label }}</span>
                <input type="radio" [(ngModel)]="formData.phq_9" name="phq_9" [value]="opt.val" (change)="onScoreChange()" class="sr-only">
              </label>
            </div>
            <p class="text-[11px] text-rose-800 leading-relaxed bg-white/70 p-2.5 rounded-lg border border-rose-200/60">
              💚 If you are carrying heavy emotional pain, you are never alone and never a burden. Warm human support is always available whenever you need to talk.
            </p>
          </div>
        </div>
      </section>

      <!-- SECTION 4: Student Pressure & Stress Triggers (Q21 - Q22) -->
      <section class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs border border-emerald-100/80">
        <div class="flex items-center gap-3 mb-5 pb-3.5 border-b border-slate-100">
          <span class="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white font-bold flex items-center justify-center text-xs sm:text-sm shadow-xs">4</span>
          <div>
            <h3 class="text-base sm:text-lg font-bold text-slate-900">What is causing you the most stress on campus?</h3>
            <p class="text-xs text-slate-500">Select what is currently weighing heavily on you.</p>
          </div>
        </div>

        <div class="space-y-5">
          <!-- Q21 -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-2">
              21. Which of these are your biggest stress triggers right now? <span class="text-slate-400 font-normal">(Pick all that apply)</span>
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 text-xs sm:text-sm">
              <label *ngFor="let trig of [
                { val: 'Coursework overload & competing deadlines', label: 'Piles of schoolwork, presentations & impossible deadlines' },
                { val: 'Fear of exam failure & GPA anxiety', label: 'Fear of failing exams, bad grades, or academic probation' },
                { val: 'Part-time job & lecture exhaustion', label: 'Exhaustion from juggling a part-time job or side hustle with classes' },
                { val: 'Tuition & student living expenses', label: 'Tuition fees, hostel rent, food costs & financial pressure' },
                { val: 'Lecturer pressure & demanding supervisors', label: 'Harsh professors, difficult grading, or project supervisors' },
                { val: 'Imposter syndrome & peer comparison', label: 'Imposter syndrome (feeling behind your coursemates or like a fake)' }
              ]" class="option-card flex items-start gap-2.5 p-3 rounded-xl sm:rounded-2xl border cursor-pointer"
                 [ngClass]="isChecked('academic_stressors', trig.val) ? 'border-emerald-500 bg-emerald-50/70 font-semibold text-emerald-950 ring-1 ring-emerald-500/30' : 'border-slate-200 bg-slate-50/30 hover:bg-slate-50 text-slate-700'">
                <input type="checkbox" (change)="toggleCheckbox('academic_stressors', trig.val)" [checked]="isChecked('academic_stressors', trig.val)" class="rounded text-emerald-600 accent-emerald-600 mt-0.5">
                <span class="leading-snug">{{ trig.label }}</span>
              </label>
            </div>
          </div>

          <!-- Q22 -->
          <div>
            <label class="block text-xs sm:text-sm font-semibold text-slate-800 mb-2">
              22. How does this stress show up in your body and daily life? <span class="text-slate-400 font-normal">(Pick all that apply)</span>
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5 text-xs sm:text-sm">
              <label *ngFor="let sym of [
                { val: 'Executive paralysis & inability to start', label: 'Feeling frozen — wanting to work but staring at the screen stuck' },
                { val: 'Racing heart, chest tightness, shallow breath', label: 'Heart beating fast, tight chest, or hard to breathe' },
                { val: 'Severe brain fog & emotional numbness', label: 'Brain fog, zoning out, or feeling completely numb' },
                { val: 'Social withdrawal & avoiding contact', label: 'Ghosting friends, ignoring calls, or hiding away in bed' }
              ]" class="option-card flex items-start gap-2.5 p-3 rounded-xl sm:rounded-2xl border cursor-pointer"
                 [ngClass]="isChecked('physical_symptoms', sym.val) ? 'border-emerald-500 bg-emerald-50/70 font-semibold text-emerald-950 ring-1 ring-emerald-500/30' : 'border-slate-200 bg-slate-50/30 hover:bg-slate-50 text-slate-700'">
                <input type="checkbox" (change)="toggleCheckbox('physical_symptoms', sym.val)" [checked]="isChecked('physical_symptoms', sym.val)" class="rounded text-emerald-600 accent-emerald-600 mt-0.5">
                <span class="leading-snug">{{ sym.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      <!-- SECTION 5: Midnight Vent & AI Empathy Training (Q23 - Q24b) -->
      <section class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs border border-emerald-100/80">
        <div class="flex items-center gap-3 mb-5 pb-3.5 border-b border-slate-100">
          <span class="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-indigo-600 to-slate-800 text-white font-bold flex items-center justify-center text-xs sm:text-sm shadow-xs">5</span>
          <div>
            <h3 class="text-base sm:text-lg font-bold text-slate-900">Teach Joy AI How to Speak to Students</h3>
            <p class="text-xs text-slate-500">Your exact words will train the AI to respond with real empathy and comfort.</p>
          </div>
        </div>

        <div class="space-y-5">
          <!-- Q23: The Midnight Vent -->
          <div class="bg-slate-900/95 text-white p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-700">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="text-amber-400 text-base">🌙</span>
              <label class="text-xs sm:text-sm font-bold text-slate-100">
                23. The Midnight Vent: What would you type to JoyBot at your lowest? <span class="text-rose-400">*</span>
              </label>
            </div>
            <p class="text-[11px] sm:text-xs text-slate-400 mb-2.5 leading-relaxed">
              Imagine it is 1:00 AM after an exhausting, stressful day on campus. You open the chat to vent. In your everyday words, what would you say? (Don't worry about grammar — just write what you feel).
            </p>
            <textarea [(ngModel)]="formData.late_night_message" name="late_night_message" (input)="calculateProgress()" rows="3" required
                      placeholder="e.g., I'm so exhausted and my head hurts. I have 3 assignment deadlines tomorrow and I can't even get myself to open my laptop. I just feel so useless right now..."
                      class="custom-input w-full px-3.5 py-2.5 sm:py-3 rounded-xl border border-slate-700 bg-slate-800/90 text-white placeholder-slate-500 text-xs sm:text-sm outline-none leading-relaxed"></textarea>
          </div>

          <!-- Q24a: The Right Response -->
          <div class="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-emerald-200 bg-emerald-50/40">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="text-emerald-600 text-base">💚</span>
              <label class="text-xs sm:text-sm font-bold text-slate-900">
                24a. The Right Response: What kind of reply would actually help you feel calmer? <span class="text-rose-500">*</span>
              </label>
            </div>
            <p class="text-[11px] sm:text-xs text-slate-600 mb-2.5 leading-relaxed">
              When you send that message, what kind of response from an AI would bring you genuine emotional relief?
            </p>
            <textarea [(ngModel)]="formData.ideal_response_feedback" name="ideal_response_feedback" (input)="calculateProgress()" rows="3" required
                      placeholder="e.g., Don't lecture me or give me advice right away. Just tell me it's okay to feel overwhelmed, that I'm not a failure, and help me take one slow, calm breath..."
                      class="custom-input w-full px-3.5 py-2.5 sm:py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-xs sm:text-sm outline-none leading-relaxed"></textarea>
          </div>

          <!-- Q24b: What NOT to say -->
          <div class="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-rose-200 bg-rose-50/40">
            <div class="flex items-center gap-2 mb-1.5">
              <span class="text-rose-600 text-base">🛡️</span>
              <label class="text-xs sm:text-sm font-bold text-slate-900">
                24b. What NOT to say: What kind of answers make you feel worse or annoyed? <span class="text-rose-500">*</span>
              </label>
            </div>
            <p class="text-[11px] sm:text-xs text-slate-600 mb-2.5 leading-relaxed">
              What cliché phrases or attitudes make you roll your eyes or feel invalidated?
            </p>
            <textarea [(ngModel)]="formData.avoid_patterns_feedback" name="avoid_patterns_feedback" (input)="calculateProgress()" rows="2" required
                      placeholder="e.g., Don't tell me 'just think positive', don't compare me to people who have it worse, and don't dump a 10-step productivity list on me..."
                      class="custom-input w-full px-3.5 py-2.5 sm:py-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-xs sm:text-sm outline-none leading-relaxed"></textarea>
          </div>
        </div>
      </section>

      <!-- Submit Action Card -->
      <div class="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-sm border border-emerald-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <p class="text-sm sm:text-base font-bold text-slate-900">Ready to submit your responses?</p>
          <p class="text-xs text-slate-500 mt-0.5">Thank you for sharing your truth. Your input directly empowers Joy AI to support university students.</p>
        </div>
        <button type="submit" [disabled]="isSubmitting"
                class="w-full sm:w-auto min-h-[48px] px-8 py-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-700 hover:to-teal-700 text-white font-bold rounded-xl sm:rounded-2xl shadow-lg shadow-emerald-600/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
          <svg *ngIf="isSubmitting" class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <span>{{ isSubmitting ? 'Saving your responses...' : 'Submit My Responses' }}</span>
          <svg *ngIf="!isSubmitting" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
          </svg>
        </button>
      </div>
    </form>

    <!-- Success Celebration Card -->
    <div *ngIf="isSubmitted" class="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 text-center shadow-xl border border-emerald-200 my-6 sm:my-8 animate-fadeIn">
      <div class="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-3xl sm:text-4xl mx-auto mb-4 sm:mb-6 shadow-inner">
        🌿
      </div>
      <h2 class="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">Thank You So Much!</h2>
      <p class="text-slate-600 max-w-lg mx-auto text-xs sm:text-sm md:text-base leading-relaxed mb-6">
        Your answers have been securely recorded. What you shared today will directly help Joy AI respond with genuine empathy, warmth, and care whenever a student reaches out for support.
      </p>

      <div class="max-w-md mx-auto bg-emerald-50/60 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-emerald-200 mb-6 text-left text-xs space-y-2">
        <p class="font-bold text-slate-800 text-xs sm:text-sm border-b border-emerald-200/60 pb-1.5">Your Check-in Snapshot:</p>
        <p>• <strong>Anxiety & Worry Level:</strong> <span class="font-bold text-emerald-800">{{ friendlyGadLevel }}</span></p>
        <p>• <strong>Mood & Energy Level:</strong> <span class="font-bold text-teal-800">{{ friendlyPhqLevel }}</span></p>
        <p>• <strong>Submission Reference:</strong> <span class="font-mono text-slate-500">{{ submissionRef }}</span></p>
      </div>

      <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
        <a routerLink="/" class="w-full sm:w-auto px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition shadow-md shadow-emerald-600/20 text-center">
          Chat with JoyBot Now
        </a>
        <button (click)="resetForm()" class="w-full sm:w-auto px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition">
          Fill Again
        </button>
      </div>
    </div>

  </main>
</div>
